import { keepPreviousData } from "@tanstack/react-query";
import { TxAction, TxStep, useContractStaticCall, useContractView, useHandleTx, useTxSteps } from "logic/hooks";
import { bigIntMax } from "logic/utils";
import { useEffect } from "react";
import { erc20Abi } from "viem";

import { LOAN_ADDRESSES, LOAN_ROUTER_V2_ABI, ReserveAccountAbi } from "../contracts";
import { Loan } from "../data/types";
import { useLoanDetails } from "../LoanDetails/LoanDetailsProvider";

/**
 * `isEnabled` false suppresses the simulations. Set it when the entered amount
 * is known-invalid: `repay` reverts with `InvalidAmount` below the total due, and
 * simulating anyway surfaces that raw contract error as a step error next to the
 * validation message that already explains the problem.
 */
export function useRepayTx(loan: Loan, customPaymentAmount?: bigint, transferAmount?: bigint, isEnabled = true) {
  const { chainId } = loan;
  const { balances, reserveAccount: resAccAddress, reservesRequired, tokens } = useLoanDetails();

  const isPrepayment = Boolean(customPaymentAmount);
  const needsTransfer = transferAmount != undefined && transferAmount > 0n;

  const paymentAmount = usePaymentQuote(loan, customPaymentAmount);

  // Mirrors resolveRepaymentFlow: `required` is held back for upcoming payments,
  // so only the remainder can fund this one. The two must agree, otherwise the
  // flow asks for a transfer while this gate already calls the balance enough.
  const availableResAccUsdai =
    balances?.reserve.usdaiMain != undefined && reservesRequired != undefined
      ? bigIntMax(balances.reserve.usdaiMain - reservesRequired, 0n)
      : undefined;

  const isResAccBalanceEnough =
    availableResAccUsdai != undefined && paymentAmount != undefined && availableResAccUsdai >= paymentAmount;
  const isBalanceInsufficient = !isResAccBalanceEnough;

  const { steps, setSteps, updateStepAtIndex, currentStepIndex } = useTxSteps(
    getInitialSteps(needsTransfer, isPrepayment),
  );
  const handleTx = useHandleTx();

  const transferStepIndex = 0;
  const transferWentThrough = steps.length > 1 && steps[transferStepIndex]?.status !== "idle";
  const isTransferConfirmed = steps[transferStepIndex]?.status === "confirmed";
  const repayStepIndex = steps.length - 1;

  const { data: transferData, error: transferError } = useContractStaticCall(
    needsTransfer
      ? {
          address: tokens.usdai.main.address,
          abi: erc20Abi,
          functionName: "transfer",
          args: [resAccAddress, transferAmount],
          chainId,
          query: {
            enabled: isEnabled && !isResAccBalanceEnough,
          },
        }
      : undefined,
  );

  const { data: repayData, error: repayError } = useContractStaticCall(
    paymentAmount
      ? {
          address: resAccAddress,
          abi: ReserveAccountAbi,
          functionName: "repay",
          args: [loan.termsStruct, paymentAmount],
          chainId,
          query: {
            enabled: isEnabled && (needsTransfer ? isTransferConfirmed : isResAccBalanceEnough),
          },
        }
      : undefined,
  );

  useEffect(() => {
    if (!transferWentThrough) setSteps(getInitialSteps(needsTransfer, isPrepayment));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsTransfer, isPrepayment]);

  async function transfer() {
    return await handleTx({
      title: "Transfer USDai",
      params: transferData?.request,
      updateStep: (updates) => updateStepAtIndex(transferStepIndex, updates),
    });
  }

  async function repay() {
    return await handleTx({
      title: isPrepayment ? "Prepay" : "Make Payment",
      params: repayData?.request,
      updateStep: (updates) => updateStepAtIndex(repayStepIndex, updates),
    });
  }

  const repayActions: TxAction[] = [{ send: repayData?.request && repay, error: repayError }];
  const transferAndRepayActions: TxAction[] = [
    { send: transferData?.request && transfer, error: transferError },
    { send: repayData?.request && repay, error: repayError },
  ];
  const actions = needsTransfer || transferWentThrough ? transferAndRepayActions : repayActions;
  const action = actions[Math.min(currentStepIndex, actions.length - 1)];

  return { action, steps, currentStepIndex, isBalanceInsufficient, paymentAmount };
}

function getInitialSteps(needsTransfer: boolean, isPrepayment: boolean): TxStep[] {
  const steps: TxStep[] = [];
  if (needsTransfer) {
    steps.push({ title: "Transfer", action: "Transfer", status: "idle" });
  }
  steps.push({
    title: isPrepayment ? "Prepay" : "Pay",
    action: isPrepayment ? "Prepay" : "Make Payment",
    status: "idle",
  });
  return steps;
}

const QUOTE_DEADLINE_OFFSET_SEC = 180;
const QUOTE_BUCKET_SEC = 30;

// Snap to a 30s wall-clock bucket so `deadline` (and therefore the queryKey)
// is stable across renders inside one bucket. Previously this was derived
// from a per-block `useBlock`, which made the queryKey churn every ~8s and
// defeated the 30s `refetchInterval` below — so `paymentAmount` ticked up
// (interest accrual) far more often than intended, contributing to the
// "Insufficient Balance" flip on the repay button. See useBridgeAndRepay.ts
// for the active instrumentation tracking which mechanism is actually firing.
export function usePaymentQuote(loan: Loan, customPaymentAmount?: bigint): bigint | undefined {
  const { chainId } = loan;
  const { loanRouterV2 } = LOAN_ADDRESSES[chainId];

  const nowSec = Math.floor(Date.now() / 1000);
  const bucketSec = nowSec - (nowSec % QUOTE_BUCKET_SEC);
  const deadline = BigInt(bucketSec + QUOTE_DEADLINE_OFFSET_SEC);

  const { data: quote } = useContractView({
    address: loanRouterV2,
    abi: LOAN_ROUTER_V2_ABI,
    functionName: "quote",
    args: [loan.termsStruct, deadline],
    chainId,
    query: {
      placeholderData: keepPreviousData,
      select: (data) => BigInt(data[0] + data[1] + data[2]),
      refetchInterval: 30_000,
      enabled: customPaymentAmount == undefined,
    },
  });

  return customPaymentAmount ?? quote;
}
